import { motion, useInView } from "motion/react";
import { useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { PageMeta } from "../PageMeta";
import { Breadcrumbs } from "../Breadcrumbs";

export function PricingPage() {
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "annual">("monthly");

  const heroRef = useRef(null);
  const heroInView = useInView(heroRef, { once: true, amount: 0.1 });

  const tiersRef = useRef(null);
  const tiersInView = useInView(tiersRef, { once: true, amount: 0.05 });

  const faqRef = useRef(null);
  const faqInView = useInView(faqRef, { once: true, amount: 0.05 });

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      <PageMeta
        title="Pricing"
        description="Transparent pricing for AI governance. Free tier with 1,000 decisions/month. Enterprise plans with custom SLAs, EU data residency, and dedicated support."
        keywords="AI governance pricing, EU AI Act compliance pricing, audit trail pricing, confidence scoring costs, enterprise AI security"
        ogImage="/og-pricing.png"
      />
      
      {/* Hero Section */}
      <section ref={heroRef} className="relative border-b border-[var(--border)] section-padding">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-radial from-[var(--premium)]/5 via-transparent to-transparent pointer-events-none" />

        <div className="mx-auto relative z-10 page-responsive">
          {/* Breadcrumbs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
            className="mb-6"
          >
            <Breadcrumbs
              items={[
                { label: "Pricing" }
              ]}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
          >
            {/* Eyebrow label */}
            <motion.span
              className="font-mono uppercase tracking-[0.2em] text-[var(--premium)] mb-6 block eyebrow"
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
            >
              Pricing
            </motion.span>

            {/* H1 */}
            <motion.h1
              className="font-semibold mb-8 h1-hero"
              style={{
                lineHeight: "1.05",
                letterSpacing: "-0.03em",
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
            >
              Transparent pricing for absolute certainty.
            </motion.h1>
          </motion.div>

          {/* Intro */}
          <motion.p
            className="text-[var(--muted-foreground)] max-w-4xl body-large"
            style={{
              lineHeight: "1.6",
              letterSpacing: "0.01em",
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.4, 0, 0.2, 1] }}
          >
            Predictable usage-based models. No hidden seats, no complex token math. You pay per evaluated decision trace.
          </motion.p>
        </div>
      </section>

      {/* Pricing Tiers Section */}
      <section
        ref={tiersRef}
        className="relative border-b border-[var(--border)] section-padding"
      >
        <div className="mx-auto page-responsive">
          {/* Billing Toggle */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={tiersInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
            className="flex justify-center items-center gap-4 mb-12"
          >
            {/* Toggle Switch */}
            <div className="relative inline-flex border border-[var(--border)] bg-[var(--surface-1)]">
              {/* Animated Slider Background - APPLE SMOOTH */}
              <motion.div
                className="absolute top-0 bottom-0 bg-[var(--premium)]"
                animate={{
                  left: billingPeriod === "monthly" ? 0 : "50%",
                  right: billingPeriod === "monthly" ? "50%" : 0,
                }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 30,
                }}
              />

              <button
                onClick={() => setBillingPeriod("monthly")}
                className={`
                  relative z-10 px-8 py-3 text-sm font-medium transition-all duration-300
                  ${
                    billingPeriod === "monthly"
                      ? "text-[var(--background)]"
                      : "text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
                  }
                `}
              >
                Monthly
              </button>

              <button
                onClick={() => setBillingPeriod("annual")}
                className={`
                  relative z-10 px-8 py-3 text-sm font-medium transition-all duration-300
                  ${
                    billingPeriod === "annual"
                      ? "text-[var(--background)]"
                      : "text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
                  }
                `}
              >
                Annual
              </button>
            </div>

            {/* Side Label - GOVERNMENT GRADE */}
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
              className={`
                px-4 py-3 text-sm font-medium border transition-all duration-300
                ${
                  billingPeriod === "annual"
                    ? "border-[var(--confidence)] text-[var(--confidence)] bg-[var(--confidence)]/5"
                    : "border-[var(--border)] text-[var(--muted-foreground)]"
                }
              `}
            >
              Save 20%
            </motion.div>
          </motion.div>

          {/* Pricing Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-12">
            {/* Developer Tier */}
            <PricingCard
              tier="Developer"
              price="Free"
              period=""
              limit="1,000 decisions / month"
              description="Evaluate ADJUDON with real traffic, no pressure."
              features={[
                "1 AI Agent",
                "1 Workspace",
                "Basic Confidence Scoring",
                "Decision Log (7 days)",
                "REST API Access",
                "Community Support",
              ]}
              cta="Start for Free"
              ctaLink="/signup"
              isInView={tiersInView}
              delay={0}
              billingPeriod={billingPeriod}
            />

            {/* Scale Tier */}
            <PricingCard
              tier="Scale"
              price={billingPeriod === "monthly" ? "€249" : "€199"}
              originalPrice={billingPeriod === "annual" ? "€249" : undefined}
              period={billingPeriod === "monthly" ? "per month" : "per month"}
              billingNote={billingPeriod === "annual" ? "Billed annually · Save 20%" : undefined}
              limit="100,000 decisions / month · €0.003 / overage"
              description="For teams running AI agents in production."
              badge="Most Popular"
              features={[
                "Unlimited AI Agents",
                "3 Workspaces",
                "3-Pillar CPI Scoring",
                "Policy Engine (block / flag / approve)",
                "Alert Engine (Slack, Email, Webhook)",
                "Automated PII Masking",
                "Data Retention (90 days)",
                "CSV Export & Audit Logs",
                "Priority Email Support",
              ]}
              cta="Start 14-Day Trial"
              ctaLink="/signup?plan=scale"
              isInView={tiersInView}
              delay={0.1}
              highlighted
              billingPeriod={billingPeriod}
            />

            {/* Governance Tier */}
            <PricingCard
              tier="Governance"
              price={billingPeriod === "monthly" ? "€899" : "€719"}
              originalPrice={billingPeriod === "annual" ? "€899" : undefined}
              period={billingPeriod === "monthly" ? "per month" : "per month"}
              billingNote={billingPeriod === "annual" ? "Billed annually · Save 20%" : undefined}
              limit="500,000 decisions / month · €0.001 / overage"
              description="Full compliance stack for regulated industries."
              features={[
                "Unlimited AI Agents",
                "Unlimited Workspaces",
                "All Scale features",
                "SSO / SAML",
                "Human-in-the-Loop Review Queue",
                "Vector Memory + Semantic Search",
                "Scheduled Compliance Reports",
                "Custom Data Retention (7–365 days)",
                "99.9% SLA",
                "Priority Support",
              ]}
              cta="Book Technical Demo"
              ctaLink="/contact?demo=governance"
              isInView={tiersInView}
              delay={0.2}
              billingPeriod={billingPeriod}
            />

            {/* Enterprise Tier - now as a card with price anchor */}
            <PricingCard
              tier="Enterprise"
              price={billingPeriod === "monthly" ? "€1,999" : "€1,599"}
              originalPrice={billingPeriod === "annual" ? "€1,999" : undefined}
              period={billingPeriod === "monthly" ? "per month" : "per month"}
              billingNote={billingPeriod === "annual" ? "Billed annually · Save 20%" : undefined}
              limit="2,000,000 decisions / month · €0.0005 / overage"
              description="Advanced governance for large AI deployments."
              features={[
                "All Professional features",
                "Decision Mining (HITL)",
                "CPI Feedback Ingest",
                "Audit-ready PDF export",
                "365+ day custom retention",
                "Dedicated CSM & SLA",
              ]}
              cta="Contact Sales"
              ctaLink="/contact?plan=enterprise"
              isInView={tiersInView}
              delay={0.3}
              billingPeriod={billingPeriod}
            />
          </div>

          {/* Custom / White-glove callout */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={tiersInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="relative border border-[var(--border)] bg-[var(--surface-1)] p-8 md:p-12 mb-12"
          >
            {/* Top accent line - ANIMATED */}
            <motion.div
              className="absolute top-0 left-0 right-0 h-[2px] bg-[var(--premium)]"
              style={{ transformOrigin: "left" }}
              initial={{ scaleX: 0 }}
              animate={tiersInView ? { scaleX: 1 } : { scaleX: 0 }}
              transition={{ duration: 0.6, delay: 0.5, ease: [0.4, 0, 0.2, 1] }}
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <div>
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h3
                    className="text-3xl md:text-4xl font-semibold"
                    style={{
                      lineHeight: "1.1",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Custom
                  </h3>
                  <span
                    className="px-3 py-1 text-xs font-mono uppercase tracking-[0.15em] border flex-shrink-0"
                    style={{
                      borderColor: "var(--premium)",
                      color: "var(--premium)",
                      backgroundColor: "rgba(168, 85, 247, 0.1)",
                    }}
                  >
                    Custom Pricing
                  </span>
                </div>

                <p
                  className="text-base md:text-lg text-[var(--muted-foreground)] mb-8"
                  style={{ lineHeight: "1.6" }}
                >
                  For organizations with 10M+ decisions/month, strict regulatory requirements, on-premise mandates, or custom SLA needs.
                </p>

                <a
                  href="/contact?plan=enterprise"
                  className="inline-block px-8 py-4 bg-[var(--premium)] text-[var(--background)] font-medium transition-all duration-200 hover:bg-[var(--premium)]/90"
                >
                  Contact Sales
                </a>
              </div>

              <div>
                <h4
                  className="text-sm font-mono uppercase tracking-[0.15em] text-[var(--muted-foreground)] mb-6"
                >
                  Enterprise Features
                </h4>
                <ul className="space-y-3">
                  {[
                    "Unlimited decision volume",
                    "Dedicated infrastructure / on-premise",
                    "White-label dashboard",
                    "Custom SLA & support contract",
                    "BaFin / GDPR regulator liaison support",
                    "All Enterprise features",
                  ].map((feature, index) => (
                    <li
                      key={index}
                      className="flex items-start gap-3 text-sm md:text-base text-[var(--foreground)]"
                      style={{ lineHeight: "1.6" }}
                    >
                      <Check className="w-5 h-5 text-[var(--premium)] flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Trust Row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={tiersInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.7, delay: 0.4, ease: [0.4, 0, 0.2, 1] }}
            className="flex flex-wrap items-center justify-center gap-4 md:gap-8 text-sm text-[var(--muted-foreground)]"
          >
            {[
              "14-day free trial",
              "No credit card required",
              "Cancel anytime",
              "EU data residency",
              "SOC 2 Type II (planned)",
            ].map((item, index) => (
              <span key={index} className="flex items-center gap-2">
                {index > 0 && <span className="text-[var(--border)] hidden md:inline">•</span>}
                {item}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section
        ref={faqRef}
        className="relative section-padding"
      >
        <div className="mx-auto" style={{ width: "min(90vw, 3000px)" }}>
          <motion.h2
            className="text-4xl md:text-5xl lg:text-6xl font-semibold mb-12"
            style={{
              lineHeight: "1.1",
              letterSpacing: "-0.02em",
            }}
            initial={{ opacity: 0, y: 30 }}
            animate={faqInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
          >
            Frequently Asked Questions
          </motion.h2>

          <div className="space-y-6">
            <FAQCard
              question='What counts as a "decision trace"?'
              answer="One trace equals one API call to POST /api/traces (or one call via our SDK's ingestTrace method). A single user interaction may trigger multiple traces depending on how many sub-decisions your agent makes."
              isInView={faqInView}
              delay={0}
            />

            <FAQCard
              question="What happens if I exceed my monthly trace limit?"
              answer="You are never blocked. We charge the defined overage rate per additional trace at the end of the billing period. There are no surprise cut-offs — your AI agents keep running."
              isInView={faqInView}
              delay={0.1}
            />

            <FAQCard
              question="Do you train models on my trace data?"
              answer="Absolutely not. Your trace payloads and metadata belong exclusively to you. We act strictly as a data processor under GDPR and never use customer data to improve our generalized models."
              isInView={faqInView}
              delay={0.2}
            />

            <FAQCard
              question="Can we run a proof of concept before committing?"
              answer="Yes. You can capture and inspect all agent decisions in the dashboard before enforcing policies. It is the fastest path to demonstrating compliance value to your legal team without disrupting production workflows."
              isInView={faqInView}
              delay={0.3}
            />
          </div>
        </div>
      </section>
    </div>
  );
}

// PricingCard Component
function PricingCard({
  tier,
  price,
  originalPrice,
  period,
  billingNote,
  limit,
  description,
  badge,
  features,
  cta,
  ctaLink,
  isInView,
  delay,
  highlighted = false,
  billingPeriod: _billingPeriod,
}: {
  tier: string;
  price: string;
  originalPrice?: string;
  period: string;
  billingNote?: string;
  limit: string;
  description: string;
  badge?: string;
  features: string[];
  cta: string;
  ctaLink: string;
  isInView: boolean;
  delay: number;
  highlighted?: boolean;
  billingPeriod: "monthly" | "annual";
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.8, delay, ease: [0.4, 0, 0.2, 1] }}
      className={`
        relative border bg-[var(--surface-1)] p-6 md:p-8 flex flex-col h-full
        ${highlighted ? "border-[var(--premium)]" : "border-[var(--border)]"}
      `}
    >
      {/* Top accent line - ANIMATED */}
      <motion.div
        className={`absolute top-0 left-0 right-0 h-[2px] ${
          highlighted ? "bg-[var(--premium)]" : "bg-[var(--system-cyan)]"
        }`}
        style={{ transformOrigin: "left" }}
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: false }}
        transition={{ duration: 0.6, delay: delay * 0.1, ease: [0.4, 0, 0.2, 1] }}
      />

      {/* Tier Name + Badge Row */}
      <div className="flex items-center justify-between mb-2">
        <h3
          className="text-2xl md:text-3xl font-semibold"
          style={{
            lineHeight: "1.2",
            letterSpacing: "-0.01em",
          }}
        >
          {tier}
        </h3>
        
        {/* Badge ganz rechts */}
        {badge && (
          <span
            className="px-3 py-1 text-xs font-mono uppercase tracking-[0.15em] border flex-shrink-0"
            style={{
              borderColor: "var(--premium)",
              color: "var(--premium)",
              backgroundColor: "rgba(168, 85, 247, 0.1)",
            }}
          >
            {badge}
          </span>
        )}
      </div>

      {/* Limit */}
      <p
        className="text-sm text-[var(--muted-foreground)] mb-2"
        style={{ lineHeight: "1.6" }}
      >
        {limit}
      </p>

      {/* Description */}
      <p
        className="text-sm text-[var(--muted-foreground)] mb-6"
        style={{ lineHeight: "1.6" }}
      >
        {description}
      </p>

      {/* Price */}
      <div className="mb-6">
        <div className="flex items-baseline gap-2 mb-1">
          <span
            className="text-4xl md:text-5xl font-semibold"
            style={{ letterSpacing: "-0.02em" }}
          >
            {price}
          </span>
          {period && (
            <span className="text-base text-[var(--muted-foreground)]">{period}</span>
          )}
        </div>
        {originalPrice && (
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm text-[var(--muted-foreground)] line-through">
              {originalPrice}/mo
            </span>
          </div>
        )}
        {billingNote && (
          <span className="text-xs text-[var(--muted-foreground)]">{billingNote}</span>
        )}
      </div>

      {/* Features */}
      <ul className="space-y-3 mb-8 flex-1">
        {features.map((feature, index) => (
          <li
            key={index}
            className="flex items-start gap-3 text-sm text-[var(--foreground)]"
            style={{ lineHeight: "1.6" }}
          >
            <Check
              className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                highlighted ? "text-[var(--premium)]" : "text-[var(--system-cyan)]"
              }`}
            />
            {feature}
          </li>
        ))}
      </ul>

      {/* CTA */}
      <a
        href={ctaLink}
        className={`
          block text-center px-6 py-3 font-medium transition-all duration-200
          ${
            highlighted
              ? "bg-[var(--premium)] text-[var(--background)] hover:bg-[var(--premium)]/90"
              : "border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--surface-2)]"
          }
        `}
      >
        {cta}
      </a>
    </motion.div>
  );
}

// FAQCard Component
function FAQCard({
  question,
  answer,
  isInView,
  delay,
}: {
  question: string;
  answer: string;
  isInView: boolean;
  delay: number;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      className="relative border border-[var(--border)] bg-[var(--surface-1)] overflow-hidden"
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{
        duration: 0.5,
        delay: delay,
        ease: [0.4, 0, 0.2, 1],
      }}
    >
      {/* Top accent line */}
      <motion.div
        className="absolute top-0 left-0 right-0 h-[2px] bg-[var(--premium)]"
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        transition={{ duration: 0.6, delay: 0.2 + delay }}
        style={{ transformOrigin: "left" }}
      />

      {/* Question - Always visible, clickable */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full text-left p-6 md:p-8 flex items-center justify-between gap-4 group transition-colors duration-200 hover:bg-[var(--surface-2)]"
      >
        <h3
          className="text-lg md:text-xl font-semibold"
          style={{
            lineHeight: "1.3",
            letterSpacing: "-0.01em",
          }}
        >
          {question}
        </h3>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
        >
          <ChevronDown
            className="w-5 h-5 text-[var(--muted-foreground)] transition-colors duration-200 group-hover:text-[var(--premium)] flex-shrink-0"
          />
        </motion.div>
      </button>

      {/* Answer - Expandable */}
      <motion.div
        initial={false}
        animate={{
          height: isOpen ? "auto" : 0,
          opacity: isOpen ? 1 : 0,
        }}
        transition={{
          height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
          opacity: { duration: 0.2, ease: [0.4, 0, 0.2, 1] },
        }}
        className="overflow-hidden"
      >
        <div className="px-6 md:px-8 pb-6 md:pb-8 border-t border-[var(--border)]">
          <p
            className="text-base text-[var(--muted-foreground)] mt-6"
            style={{
              lineHeight: "1.7",
              letterSpacing: "0.005em",
            }}
          >
            {answer}
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}